import axios, { Canceler, AxiosResponse, AxiosRequestConfig, AxiosError } from 'axios'
import aqe from '@pushly/aqe'
import { Container } from 'typescript-ioc/es5'
import { AppState } from '@halo/stores/app.state'
import { AuthService } from '@halo/services/auth.service'
import { notification } from 'antd'
import { shouldUseLegacyAuthHeader } from '@halo/_utils/session-helpers'
import { PushlyCookie } from '@halo/enums/pushly-cookie.enum'

export function isLocalEnv() {
    return process.env.LOCAL_ENV?.toString() === 'true'
}

function haloRequestInterceptor(request: any) {
    if (request.external === true) {
        return request
    }

    request.withCredentials = true
    request.headers['X-Application-Source'] = 'halo'
    request.headers['X-Application-Version'] = 'halo'

    if (shouldUseLegacyAuthHeader()) {
        request.headers.Authorization = `Bearer ${window.localStorage.getItem(PushlyCookie.PLATFORM_COOKIE)}`
    }

    return request
}

function haloReponseErrorInterceptor(error: any) {
    const errorRes = error.response

    if (errorRes) {
        const resConfig: any = errorRes.config ?? {}
        const errorType: string = errorRes.data?.error_type ?? 'unknown'

        const appState: AppState = Container.get(AppState)
        const authSvc: AuthService = Container.get(AuthService)

        if (!resConfig.bypassGlobalErrorHandlers) {
            if (errorRes.status === 401 && /auth_token_(expired|missing)/i.test(errorType)) {
                if (!isLocalEnv() && appState?.isAuthenticated) {
                    authSvc.logout(true)
                }
            }
            if (errorRes.status === 429) {
                const errMsg = errorRes.data?.message ?? 'Please reduce your request rate.'
                const notifDescription = `Pushly was unable to complete your request. ${errMsg.replace(/\.$/, '')}.`

                notification.error({
                    key: 'rate-limit-exception',
                    className: 'simple-notification',
                    message: undefined,
                    description: notifDescription,
                    duration: 30,
                })
            }
        }
    }

    throw error
}

export const setupAxios = () => {
    axios.interceptors.request.use(haloRequestInterceptor)
    aqe.useAxiosInterceptor('request', haloRequestInterceptor)

    // pass through for success
    // implement global error handlers
    axios.interceptors.response.use((res) => res, haloReponseErrorInterceptor)
    aqe.useAxiosInterceptor('response', (res) => res, haloReponseErrorInterceptor)
}

export const axiosCancellationRequests: { [index: string]: Canceler } = {}
export const isAxiosCancellation = (error: any) => axios.isCancel(error)
export const isAxiosResponseError = (error: Error): error is AxiosError => !!(error as AxiosError).response

export type AxiosMethod = 'get' | 'put' | 'post' | 'patch' | 'delete'
interface IAxiosRequest {
    url: string
    data?: any
    config?: AxiosRequestConfig & { cancelToken?: any }
}

export async function axiosFetch(
    method: AxiosMethod,
    pack: IAxiosRequest,
    cancellationKey?: string,
): Promise<AxiosResponse<any>> {
    pack.config = pack.config || {}

    const args: any = [pack.url]
    if (pack.data) args.push(pack.data)

    if (cancellationKey) pack.config.cancelToken = await buildCancellationRequest(cancellationKey)
    args.push(pack.config)

    let fetcher = axios.get
    switch (method.toLowerCase()) {
        case 'post':
            fetcher = axios.post
            break
        case 'patch':
            fetcher = axios.patch
            break
        case 'delete':
            fetcher = axios.delete
            break
    }

    // @ts-ignore
    const response = await fetcher(...args)

    if (cancellationKey && axiosCancellationRequests[cancellationKey]) delete axiosCancellationRequests[cancellationKey]

    return response
}

const buildCancellationRequest = async (cancellationKey: string): Promise<any> => {
    const handler = axiosCancellationRequests[cancellationKey]

    if (handler) {
        await handler()
        delete axiosCancellationRequests[cancellationKey]
    }

    return new axios.CancelToken((cb) => {
        axiosCancellationRequests[cancellationKey] = cb
    })
}
